// FloatingAudioPlayer — persistent bottom-of-viewport widget rendered
// at the app root. Reads everything from AudioGuideContext, so any
// component that calls `playGuide(...)` will surface this player
// automatically. The player remains visible while the user navigates
// between routes because the underlying <audio> element + provider
// live above the React Router tree.

import React from "react";
import { Play, Pause, X, Volume2, VolumeX, Headphones, Loader2 } from "lucide-react";
import { useAudioGuide } from "@/contexts/AudioGuideContext";

const fmtTime = (s) => {
  if (!Number.isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

const FloatingAudioPlayer = () => {
  const {
    meta, isPlaying, currentTime, duration, volume, loading,
    togglePlay, seek, setVolume, stop,
  } = useAudioGuide();

  if (!meta) return null;

  const pct = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  return (
    <aside
      role="region"
      aria-label="Audio guide player"
      className="fixed bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 z-[55] w-[min(96vw,680px)] bg-[#1A1513] text-[#FDFBF7] border border-[#D4A373]/35 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.65)] backdrop-blur-sm"
      data-testid="floating-audio-player"
    >
      <div className="flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-2.5 sm:py-3">
        {/* Headphones icon — sits on the left as a visual anchor */}
        <span className="hidden sm:inline-flex items-center justify-center w-9 h-9 shrink-0 bg-[#D4A373]/15 border border-[#D4A373]/40">
          <Headphones className="w-4 h-4 text-[#D4A373]" strokeWidth={1.7} />
        </span>

        {/* Play / Pause toggle */}
        <button
          type="button"
          onClick={togglePlay}
          className="inline-flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 shrink-0 bg-[#D4A373] hover:bg-[#C7944F] text-[#1A1513] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FDFBF7]"
          aria-label={isPlaying ? "Pause audio guide" : "Play audio guide"}
          data-testid="floating-audio-toggle"
        >
          {loading
            ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
            : isPlaying
              ? <Pause className="w-4 h-4" strokeWidth={1.8} fill="currentColor" />
              : <Play className="w-4 h-4 translate-x-[1px]" strokeWidth={1.8} fill="currentColor" />}
        </button>

        {/* Titles + progress */}
        <div className="flex-1 min-w-0">
          <p
            className="text-[9px] sm:text-[10px] tracking-[0.28em] uppercase text-[#D4A373] truncate"
            data-testid="floating-audio-guide-title"
          >
            {meta.guideTitle}
          </p>
          <p
            className="text-sm leading-tight truncate"
            title={meta.propertyName}
            data-testid="floating-audio-property"
          >
            {meta.propertyName}
          </p>

          {/* Progress + time */}
          <div className="mt-1.5 sm:mt-2 flex items-center gap-2">
            <span className="text-[10px] tabular-nums text-[#FDFBF7]/60 w-9 text-right">
              {fmtTime(currentTime)}
            </span>
            <div className="relative flex-1 h-4 flex items-center group/seek">
              <span
                aria-hidden="true"
                className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[3px] bg-[#FDFBF7]/15"
              />
              <span
                aria-hidden="true"
                className="absolute left-0 top-1/2 -translate-y-1/2 h-[3px] bg-[#D4A373]"
                style={{ width: `${pct}%` }}
              />
              <input
                type="range"
                min="0"
                max={duration > 0 ? duration : 0}
                step="0.1"
                value={Math.min(currentTime, duration || 0)}
                onChange={(e) => seek(Number(e.target.value))}
                aria-label="Seek"
                className="audio-seek-input absolute inset-0 w-full h-full appearance-none bg-transparent cursor-pointer"
                data-testid="floating-audio-seek"
              />
            </div>
            <span className="text-[10px] tabular-nums text-[#FDFBF7]/60 w-9">
              {fmtTime(duration)}
            </span>
          </div>
        </div>

        {/* Volume */}
        <div className="hidden md:flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setVolume(volume > 0 ? 0 : 1)}
            className="text-[#FDFBF7]/70 hover:text-[#D4A373] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A373]"
            aria-label={volume > 0 ? "Mute" : "Unmute"}
            data-testid="floating-audio-mute"
          >
            {volume > 0 ? <Volume2 className="w-4 h-4" strokeWidth={1.6} /> : <VolumeX className="w-4 h-4" strokeWidth={1.6} />}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={volume}
            onChange={(e) => setVolume(Number(e.target.value))}
            aria-label="Volume"
            className="audio-seek-input w-20 h-4 appearance-none bg-transparent cursor-pointer"
            data-testid="floating-audio-volume"
          />
        </div>

        {/* Close / stop */}
        <button
          type="button"
          onClick={stop}
          className="inline-flex items-center justify-center w-9 h-9 shrink-0 text-[#FDFBF7]/70 hover:text-[#FDFBF7] hover:bg-[#FDFBF7]/8 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A373]"
          aria-label="Close audio guide"
          data-testid="floating-audio-close"
        >
          <X className="w-4 h-4" strokeWidth={1.7} />
        </button>
      </div>
    </aside>
  );
};

export default FloatingAudioPlayer;
